// Components==============
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import indexActivities from 'actions/activity/index';
import showAssessment from 'actions/assessment/show';
import indexPhases from 'actions/phase/index';
import indexProjectGroup from 'actions/project-group/index';
import storeProject from 'actions/project/store';
import indexReflection from 'actions/reflection/index';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import Button from 'components/UI/Button/Button';
import Summary from 'components/UI/Summary/Summary';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import Body from 'pages/Workspace/ProjectDetails/components/Summary/Body/Body';
import TopLeft from 'pages/Workspace/ProjectDetails/components/Summary/TopLeft/TopLeft';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import './Feedback.module.scss';
import FeedbackView from './Views/FeedbackView';
import Top from './components/Top/Top';
// =========================

export type AssessmentContextType = {
  loading?: boolean;
  isTeacher: boolean;
  project?: AssessorProjectEntity;
  theme?: ThemeEntity;
  activities: (ActivityEntity | undefined)[] | undefined;
  trainings: (TrainingEntity | undefined)[] | undefined;
  wrapperRef: React.RefObject<HTMLDivElement>;
  scrollToCard: (ref: React.RefObject<HTMLDivElement>) => void;
  toggled: boolean;
  onClickToggle: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  groupedSparks: AssessorSkillEntity[];
  assessment?: AssessmentEntity;
};

export const AssessmentContext = createContext({} as AssessmentContextType);

export default function Feedback() {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const history = useHistory();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(indexTraining());
    dispatch(indexProjectGroup());
    dispatch(indexActivities());
    dispatch(indexPhases());
    dispatch(indexReflection());
  }, []);

  const { assessmentId } = useParams() as {
    assessmentId: string;
  };

  const assessment = useRedux(
    (state) => state.assessment.entities.byId[assessmentId]
  );

  useEffect(() => {
    dispatch(showAssessment(assessmentId)).then((a: AssessmentEntity) => {
      // Store the project based on the data from Assessment so that we can access project related items in the EvidenceModal for example
      dispatch(storeProject({ ...a.project, fromLocal: true }));
    });
  }, [assessmentId]);

  const peerId = null;

  // Toggle to open/close all refelction cards
  const [toggled, setIsToggled] = useState(false);
  const onClickToggle = () => setIsToggled((prev) => !prev);

  const { project } = assessment;

  const theme = project?.theme;

  const images = theme?.attachments
    ?.filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path);

  const [isOpen, setIsOpen] = useState(false);

  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const activities = useRedux((state) =>
    state.activity?.entities.allIds.map(
      (id) => state.activity?.entities.byId[id]
    )
  );

  const trainings = useRedux((state) =>
    state.training?.entities.allIds.map(
      (id) => state.training?.entities.byId[id]
    )
  );

  const loading = useRedux(
    (state) =>
      state.theme?.loading ||
      state.project?.loading ||
      state.projectGroup?.loading ||
      state.activity?.loading ||
      state.phase?.loading ||
      state.assessment?.loading
  );

  const groupedSparks = assessment.skills;

  const scrollToCard = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ inline: 'start' });
  };

  const contextValues = {
    isTeacher,
    loading,
    activities,
    project,
    theme,
    trainings,
    peerId,
    wrapperRef,
    scrollToCard,
    toggled,
    setIsToggled,
    onClickToggle,
    groupedSparks,
    assessment,
  };

  return (
    <Layout grey>
      <AssessmentContext.Provider value={{ ...contextValues }}>
        <Summary
          type="assessment"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          topLeftContent={
            <TopLeft theme={theme} isOpen={isOpen} loading={loading} />
          }
          topRightContent={
            !peerId ? (
              <Button
                onClick={() =>
                  history.push(
                    `/werkplaats/project-details/${assessment.project_id}`
                  )
                }
                color="grey"
              >
                {translate('general.terms.to-project')}
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            ) : undefined
          }
          bodyContent={<Body theme={theme} />}
          images={images}
          video={theme?.youtube_link}
          isPeerReflection={!!peerId}
        />
        <Top />
        <FeedbackView />
      </AssessmentContext.Provider>
    </Layout>
  );
}
